import React, { useState, Component } from 'react';
/**
 * AssessmentReportPage — Leerlingprofiel report with tabs.
 * Redesigned for a cleaner, less cluttered look.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Target, TrendingUp, CheckCircle2, AlertCircle, BookOpen, ArrowRight, BarChart2, LayoutDashboard, Lightbulb, Zap, Sparkles, Play } from 'lucide-react';
import type { AssessmentReport, PracticeRecommendation } from '../types';
import type { ModuleId } from '../../content/types';
import { getFamilyDefinition } from '../../content/taxonomy/taxonomyHelpers';
import { MODULES } from '../../content/modules';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
interface AssessmentReportPageProps {
  report: AssessmentReport;
  onExit: () => void;
  onRetake?: () => void;
  onPracticeWeakAreas?: () => void;
  onStartPracticeForModule?: (moduleId: ModuleId) => void;
}
type Tab = 'overzicht' | 'vakken' | 'aandacht';
export function AssessmentReportPage({
  report,
  onExit,
  onRetake,
  onPracticeWeakAreas,
  onStartPracticeForModule
}: AssessmentReportPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overzicht');
  const tabs = [{
    id: 'overzicht' as Tab,
    label: 'Overzicht',
    icon: LayoutDashboard
  }, {
    id: 'vakken' as Tab,
    label: 'Per Vak',
    icon: BarChart2
  }, {
    id: 'aandacht' as Tab,
    label: 'Sterk & Zwak',
    icon: Target
  }];
  return <div className="h-screen flex flex-col bg-slate-50 overflow-hidden font-sans" data-id="element-916">
      {/* Header */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-4 shadow-sm z-10" data-id="element-917">
        <div className="w-full flex items-center justify-between" data-id="element-918">
          <div className="flex items-center gap-4" data-id="element-919">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center" data-id="element-920">
              <Trophy className="w-6 h-6 text-orange-600" data-id="element-921" />
            </div>
            <div data-id="element-922">
              <h1 className="text-xl font-black text-slate-900 tracking-tight" data-id="element-923">
                Mijn Leerlingprofiel
              </h1>
              <p className="text-sm text-slate-500 font-medium" data-id="element-924">
                Groep {report.selectedGroup} ·{' '}
                {new Date(report.completedAt).toLocaleDateString('nl-NL', {
                month: 'long',
                year: 'numeric'
              })}
              </p>
            </div>
          </div>
          <Button onClick={onExit} variant="ghost" size="sm" className="text-slate-600 hover:bg-slate-100" data-id="element-925">
            Sluiten
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex-none bg-white border-b border-slate-200 px-4" data-id="element-926">
        <div className="w-full" data-id="element-927">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-id="element-928">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-4 font-bold text-sm transition-all relative whitespace-nowrap ${isActive ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-929">
                  <Icon className={`w-4 h-4 ${isActive ? 'stroke-[2.5px]' : ''}`} data-id="element-930" />
                  <span data-id="element-931">{tab.label}</span>
                  {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600 rounded-t-full" data-id="element-932" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-slate-50/50" data-id="element-933">
        <div className="w-full p-4 sm:p-6 md:p-8 max-w-5xl mx-auto" data-id="element-934">
          <AnimatePresence mode="wait" data-id="element-935">
            {activeTab === 'overzicht' && <OverzichtTab key="overzicht" report={report} onPracticeWeakAreas={onPracticeWeakAreas} data-id="element-936" />}
            {activeTab === 'vakken' && <VakkenTab key="vakken" report={report} data-id="element-937" />}
            {activeTab === 'aandacht' && <AandachtTab key="aandacht" report={report} onStartPracticeForModule={onStartPracticeForModule} data-id="element-938" />}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-none bg-white border-t border-slate-200 p-4 z-10" data-id="element-939">
        <div className="w-full flex flex-col items-center gap-2 max-w-md mx-auto" data-id="element-940">
          {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="w-full bg-orange-500 hover:bg-orange-600 shadow-sm" data-id="element-941">
              <BookOpen className="w-5 h-5 mr-2" data-id="element-942" />
              Direct oefenen
            </Button>}
          <Button onClick={onExit} variant="ghost" size="md" className="w-full text-slate-500 hover:text-slate-700" data-id="element-943">
            Terug naar Dashboard
          </Button>
        </div>
      </div>
    </div>;
}
// ─── Tab Components ───────────────────────────────────────────
const tabMotion = {
  initial: {
    opacity: 0,
    y: 10
  },
  animate: {
    opacity: 1,
    y: 0
  },
  exit: {
    opacity: 0,
    y: -10
  }
};
function OverzichtTab({
  report,
  onPracticeWeakAreas
}: {
  report: AssessmentReport;
  onPracticeWeakAreas?: () => void;
}) {
  const {
    score,
    estimatedLevels,
    overallEstimatedLevelCode,
    overallExpectedLevelCode,
    growthIndicators,
    actionPlan
  } = report;
  return <motion.div {...tabMotion} className="space-y-8" data-id="element-944">
      {/* Compact Hero Card */}
      <Card className="p-6 bg-gradient-to-br from-orange-400 to-orange-500 text-white border-0 shadow-md" data-id="element-945">
        <div className="flex flex-col sm:flex-row items-center gap-6" data-id="element-946">
          <div className="flex-1 text-center sm:text-left" data-id="element-947">
            <div className="inline-flex items-center gap-2 bg-white/20 px-3 py-1 rounded-full text-sm font-bold mb-3" data-id="element-948">
              <Star className="w-4 h-4 text-yellow-200 fill-yellow-200" data-id="element-949" />
              <span data-id="element-950">Jouw resultaat</span>
            </div>
            <h2 className="text-3xl font-black mb-2 tracking-tight" data-id="element-951">
              Niveau {overallEstimatedLevelCode}
            </h2>
            <p className="text-orange-50 font-medium mb-5" data-id="element-952">
              {report.overallEstimatedTier === report.overallExpectedTier ? 'Je zit precies op het verwachte niveau. Goed bezig!' : report.overallEstimatedTier > report.overallExpectedTier ? 'Wauw! Je zit boven het verwachte niveau!' : 'Je bent goed op weg. Blijf oefenen!'}
            </p>
            <div className="flex flex-wrap gap-3 justify-center sm:justify-start" data-id="element-953">
              <div className="bg-white/10 rounded-lg px-4 py-2" data-id="element-954">
                <div className="text-xs text-orange-100 uppercase font-bold tracking-wider mb-0.5" data-id="element-955">
                  Score
                </div>
                <div className="text-xl font-black" data-id="element-956">{score.percentage}%</div>
              </div>
              <div className="bg-white/10 rounded-lg px-4 py-2" data-id="element-957">
                <div className="text-xs text-orange-100 uppercase font-bold tracking-wider mb-0.5" data-id="element-958">
                  Verwacht
                </div>
                <div className="text-xl font-black" data-id="element-959">
                  {overallExpectedLevelCode}
                </div>
              </div>
            </div>
          </div>

          {/* Compact Score Ring (~100px) */}
          <div className="w-28 h-28 relative flex-shrink-0" data-id="element-960">
            <svg className="w-full h-full transform -rotate-90" data-id="element-961">
              <circle cx="50%" cy="50%" r="45%" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="8" data-id="element-962" />
              <motion.circle cx="50%" cy="50%" r="45%" fill="none" stroke="white" strokeWidth="8" strokeLinecap="round" initial={{
              pathLength: 0
            }} animate={{
              pathLength: score.percentage / 100
            }} transition={{
              duration: 1.5,
              ease: 'easeOut'
            }} data-id="element-963" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center" data-id="element-964">
              <div className="text-2xl font-black" data-id="element-965">{score.correctCount}</div>
              <div className="text-[10px] font-medium text-orange-100 uppercase tracking-wider" data-id="element-966">
                van {score.totalQuestions}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Growth Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-967">
        <GrowthCard title="Waar je groeit" items={growthIndicators.filter(g => ['groei_snel', 'gaat_goed', 'ik_groei'].includes(g.level)).slice(0, 3)} estimatedLevels={estimatedLevels} emptyText="Nog geen sterke groei zichtbaar. Blijf oefenen!" data-id="element-968" />
        <GrowthCard title="Aandachtspunten" items={growthIndicators.filter(g => ['let_op', 'extra_aandacht'].includes(g.level)).slice(0, 3)} estimatedLevels={estimatedLevels} emptyText="Geen specifieke aandachtspunten. Super!" data-id="element-969" />
      </div>

      {/* Begin Hiermee Action Card */}
      {actionPlan.beginMet && <Card className="p-6 shadow-sm border border-orange-100 bg-orange-50/50" data-id="element-970">
          <div className="flex items-center gap-2 mb-4" data-id="element-971">
            <Target className="w-5 h-5 text-orange-500" data-id="element-972" />
            <h3 className="text-lg font-bold text-slate-900" data-id="element-973">Begin hiermee</h3>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-974">
            <div data-id="element-975">
              <div className="text-sm text-slate-500 mb-1" data-id="element-976">
                {actionPlan.beginMet.moduleTitle} ›{' '}
                {actionPlan.beginMet.skillName}
              </div>
              <div className="text-base font-bold text-slate-800" data-id="element-977">
                {actionPlan.beginMet.familyDescription}
              </div>
            </div>
            {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="secondary" size="sm" className="whitespace-nowrap" data-id="element-978">
                Oefenen <ArrowRight className="w-4 h-4 ml-2" data-id="element-979" />
              </Button>}
          </div>
        </Card>}
    </motion.div>;
}
function GrowthCard({
  title,
  items,
  estimatedLevels,
  emptyText
}: {
  title: string;
  items: AssessmentReport['growthIndicators'];
  estimatedLevels: AssessmentReport['estimatedLevels'];
  emptyText: string;
}) {
  const getDotColor = (level: string) => {
    if (['groei_snel', 'gaat_goed', 'ik_groei'].includes(level)) return 'bg-emerald-500';
    if (['let_op', 'extra_aandacht'].includes(level)) return 'bg-amber-500';
    if (['krimpt', 'actie_vereist'].includes(level)) return 'bg-red-500';
    return 'bg-slate-400';
  };
  return <Card className="p-6 shadow-sm" data-id="element-980">
      <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-981">{title}</h3>
      <div className="space-y-1" data-id="element-982">
        {items.length > 0 ? items.map(g => {
        const module = estimatedLevels.find(m => m.moduleId === g.moduleId);
        return <div key={g.moduleId} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0" data-id="element-983">
                <div className="font-medium text-slate-700" data-id="element-984">
                  {module?.moduleTitle}
                </div>
                <div className="flex items-center gap-2" data-id="element-985">
                  <span className="text-sm text-slate-500" data-id="element-986">{g.label}</span>
                  <div className={`w-2.5 h-2.5 rounded-full ${getDotColor(g.level)}`} data-id="element-987" />
                </div>
              </div>;
      }) : <p className="text-slate-500 italic text-sm py-2" data-id="element-988">{emptyText}</p>}
      </div>
    </Card>;
}
function VakkenTab({
  report
}: {
  report: AssessmentReport;
}) {
  const {
    estimatedLevels,
    overallExpectedLevelCode,
    growthIndicators
  } = report;
  return <motion.div {...tabMotion} className="space-y-4" data-id="element-989">
      <Card className="overflow-hidden shadow-sm" data-id="element-990">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center" data-id="element-991">
          <h3 className="font-bold text-slate-700" data-id="element-992">Niveau per vak</h3>
          <div className="text-sm text-slate-500 flex items-center gap-2" data-id="element-993">
            <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-994" />
            Verwacht: <strong data-id="element-995">{overallExpectedLevelCode}</strong>
          </div>
        </div>
        <div className="divide-y divide-slate-100" data-id="element-996">
          {estimatedLevels.map(level => {
          const growth = growthIndicators.find(g => g.moduleId === level.moduleId);
          const isUnknown = level.questionCount < 3;
          return <div key={level.moduleId} className="p-4 sm:px-6 hover:bg-slate-50 transition-colors" data-id="element-997">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-2" data-id="element-998">
                  <div className="w-32 font-bold text-slate-900" data-id="element-999">
                    {level.moduleTitle}
                  </div>
                  <div className="flex-1" data-id="element-1000">
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden relative" data-id="element-1001">
                      <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400 z-10 border-r border-dashed border-slate-600" style={{
                    left: `${level.expectedTier / 16 * 100}%`
                  }} data-id="element-1002" />
                      <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${level.estimatedTier / 16 * 100}%`
                  }} transition={{
                    duration: 1,
                    ease: 'easeOut'
                  }} className={`h-full rounded-full ${isUnknown ? 'bg-slate-300' : level.delta >= 0 ? 'bg-emerald-500' : level.delta >= -1 ? 'bg-yellow-400' : 'bg-amber-500'}`} data-id="element-1003" />
                    </div>
                  </div>
                  <div className="w-16 text-right font-bold text-slate-700" data-id="element-1004">
                    {isUnknown ? '?' : level.estimatedLevelCode}
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm pl-0 sm:pl-36" data-id="element-1005">
                  <div className="text-slate-500" data-id="element-1006">
                    {growth?.label || 'Geen data'}
                  </div>
                  <div className="text-slate-400 text-xs" data-id="element-1007">
                    {level.questionCount} vragen
                  </div>
                </div>
              </div>;
        })}
        </div>
      </Card>
    </motion.div>;
}
function AandachtTab({
  report,
  onStartPracticeForModule
}: {
  report: AssessmentReport;
  onStartPracticeForModule?: (moduleId: ModuleId) => void;
}) {
  const {
    strengthsBySkill,
    focusAreasBySkill,
    suggestedPractice
  } = report;
  return <motion.div {...tabMotion} className="space-y-8" data-id="element-1008">
      {/* Existing strengths/weaknesses grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1009">
        <SkillCard title="Sterke Punten" icon={<Star className="w-5 h-5 text-emerald-500 fill-emerald-500" data-id="element-1011" />} areas={strengthsBySkill} emptyText="Nog geen specifieke sterke punten gevonden." itemIcon={<CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-1012" />} data-id="element-1010" />
        <SkillCard title="Aandachtspunten" icon={<Target className="w-5 h-5 text-amber-500" data-id="element-1014" />} areas={focusAreasBySkill} emptyText="Geen specifieke aandachtspunten gevonden." itemIcon={<AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1015" />} data-id="element-1013" />
      </div>

      {/* Practice Recommendations Section */}
      {suggestedPractice.length > 0 && onStartPracticeForModule && <PracticeAdviceSection recommendations={suggestedPractice} onStartPractice={onStartPracticeForModule} data-id="element-1016" />}
    </motion.div>;
}
// ─── Practice Advice Section (Feature 16) ─────────────────────
const REASON_CONFIG: Record<PracticeRecommendation['reason'], {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ReactNode;
}> = {
  weak_performance: {
    label: 'Extra oefening nodig',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50 border-amber-200',
    icon: <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-1017" />
  },
  low_mastery: {
    label: 'Nog niet beheerst',
    color: 'text-orange-700',
    bgColor: 'bg-orange-50 border-orange-200',
    icon: <Lightbulb className="w-4 h-4 text-orange-500" data-id="element-1018" />
  },
  high_potential: {
    label: 'Bijna beheerst!',
    color: 'text-sky-700',
    bgColor: 'bg-sky-50 border-sky-200',
    icon: <Sparkles className="w-4 h-4 text-sky-500" data-id="element-1019" />
  }
};
function PracticeAdviceSection({
  recommendations,
  onStartPractice
}: {
  recommendations: PracticeRecommendation[];
  onStartPractice: (moduleId: ModuleId) => void;
}) {
  // Group by module for cleaner display
  const grouped = recommendations.slice(0, 5).reduce((acc, rec) => {
    const family = getFamilyDefinition(rec.familyId);
    const moduleId = family?.module || 'Onbekend';
    if (!acc[moduleId]) acc[moduleId] = [];
    acc[moduleId].push(rec);
    return acc;
  }, {} as Record<string, PracticeRecommendation[]>);
  return <Card className="p-6 shadow-sm border-0 ring-1 ring-slate-200" data-id="element-1020">
      <div className="flex items-center gap-3 mb-2" data-id="element-1021">
        <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center" data-id="element-1022">
          <Zap className="w-5 h-5 text-orange-600" data-id="element-1023" />
        </div>
        <div data-id="element-1024">
          <h3 className="text-lg font-bold text-slate-900" data-id="element-1025">Jouw Oefenadvies</h3>
          <p className="text-sm text-slate-500" data-id="element-1026">
            Op basis van je toetsresultaten
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-5" data-id="element-1027">
        {Object.entries(grouped).map(([moduleId, recs]) => {
        const moduleTitle = MODULES.find(m => m.id === moduleId)?.title || moduleId;
        return <div key={moduleId} data-id="element-1028">
              <div className="flex items-center justify-between mb-3" data-id="element-1029">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1030">
                  {moduleTitle}
                </span>
                <button onClick={() => onStartPractice(moduleId as ModuleId)} className="text-xs font-bold text-orange-600 hover:text-orange-700 flex items-center gap-1 transition-colors" data-id="element-1031">
                  Alles oefenen
                  <ArrowRight className="w-3 h-3" data-id="element-1032" />
                </button>
              </div>
              <div className="space-y-2" data-id="element-1033">
                {recs.map(rec => {
              const config = REASON_CONFIG[rec.reason];
              return <motion.div key={rec.familyId} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.25
              }} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${config.bgColor}`} data-id="element-1034">
                      <div className="flex items-start gap-3 min-w-0 flex-1" data-id="element-1035">
                        {config.icon}
                        <div className="min-w-0" data-id="element-1036">
                          <div className="font-semibold text-slate-800 text-sm leading-tight" data-id="element-1037">
                            {rec.familyDescription}
                          </div>
                          <div className="flex items-center gap-2 mt-1" data-id="element-1038">
                            <span className={`text-xs font-medium ${config.color}`} data-id="element-1039">
                              {config.label}
                            </span>
                            <span className="text-xs text-slate-400" data-id="element-1040">
                              · {Math.round(rec.currentMastery)}% beheerst
                            </span>
                          </div>
                        </div>
                      </div>
                      <button onClick={() => {
                  const family = getFamilyDefinition(rec.familyId);
                  if (family) onStartPractice(family.module);
                }} className="flex-shrink-0 w-9 h-9 rounded-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center transition-colors shadow-sm" aria-label={`Oefen ${rec.familyDescription}`} title="Direct oefenen" data-id="element-1041">
                        <Play className="w-4 h-4 ml-0.5" data-id="element-1042" />
                      </button>
                    </motion.div>;
            })}
              </div>
            </div>;
      })}
      </div>
    </Card>;
}
function SkillCard({
  title,
  icon,
  areas,
  emptyText,
  itemIcon
}: {
  title: string;
  icon: React.ReactNode;
  areas: AssessmentReport['strengthsBySkill'];
  emptyText: string;
  itemIcon: React.ReactNode;
}) {
  return <Card className="p-6 shadow-sm h-full border-0 ring-1 ring-slate-200" data-id="element-1043">
      <div className="flex items-center gap-3 mb-6" data-id="element-1044">
        {icon}
        <h3 className="text-lg font-bold text-slate-900" data-id="element-1045">{title}</h3>
      </div>
      <div className="space-y-8" data-id="element-1046">
        {areas.length > 0 ? areas.slice(0, 3).map(area => <div key={area.moduleId} data-id="element-1047">
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-1048">
                {area.moduleTitle}
              </div>
              <ul className="space-y-4" data-id="element-1049">
                {area.skills.map(skill => <li key={skill.skillName} className="flex items-start gap-3" data-id="element-1050">
                    {itemIcon}
                    <div data-id="element-1051">
                      <div className="font-semibold text-slate-800 capitalize leading-tight mb-1" data-id="element-1052">
                        {skill.skillName}
                      </div>
                      <div className="text-sm text-slate-500" data-id="element-1053">
                        {skill.families.length} onderwerpen ·{' '}
                        {Math.round(skill.avgAccuracy)}% goed
                      </div>
                    </div>
                  </li>)}
              </ul>
            </div>) : <div className="text-center py-8 text-slate-400 italic text-sm" data-id="element-1054">
            {emptyText}
          </div>}
      </div>
    </Card>;
}